<template>
  <b-card>
    <load-profile></load-profile>
  <b-row>
    <b-col class="col-12 col-md-6 mt-1 mt-md-0 col-md-3 col-12" >
      <div v-if="isUpdate">
        <b-card-title title-tag="h2" class="font-weight-bold mb-1">
          {{ $t("updateProfile.title") }}
        </b-card-title>

        <validation-observer ref="updateProfileForm" #default="{ invalid }">
          <n-form-confirmation
            key="updateProfileForm"
            @submit="validateForm"
            :form="$refs.updateProfileForm"
            :disabled="invalid"
          >
            <n-input :fields="fields" v-model="data"> </n-input>
            <b-row>
              <b-col cols="12" class="text-right">
                <n-button-loading
                  type="submit"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="mt-1"
                  :loading="loading"
                  :disabled="invalid"
                >
                  {{ $t("button.saveChanges") }}
                </n-button-loading>
                <b-button
                  variant="outline-secondary"
                  class="ml-1 mt-1"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  @click="toggleUpdate"
                >
                  {{ $t("button.cancel") }}
                </b-button>
              </b-col>
            </b-row>
          </n-form-confirmation>
        </validation-observer>
      </div>
      <div v-else>
        <b-card-title title-tag="h2" class="font-weight-bold mb-1">
          {{ $t("general.profile") }}
        </b-card-title>

        <b-alert
          v-if="!isVerify"
          variant="info"
          dismissible
          fade
          :show="isEmailChanged"
          @dismissed="isEmailChanged = false"
        >
          <div class="alert-body text-center">
            {{ $t("general.verifyEmailSent") }}
          </div>
        </b-alert>

        <b-row class="mt-2">
          <b-col cols="auto" class="text-center">
            <b-avatar
              size="80"
              variant="light-primary"
              :src="data.profilePicture"
              :text="avatarText(data.firstName)"
            >
              <feather-icon v-if="!data.firstName" icon="UserIcon" size="22" />
            </b-avatar>
            <h5 class="mt-1">{{ data.firstName }} {{ data.lastName }}</h5>
            <h6>{{ getRoleName(data.roleNames) }}</h6>
          </b-col>
        </b-row>

        <p class="mt-2">
          <feather-icon icon="CalendarIcon" />
          {{ $t("general.joint") }}
          {{ data.createdAt | formatDate("LL") }}
        </p>
        <p><feather-icon icon="MailIcon" /> {{ data.email || '-' }}</p>
        <p><feather-icon icon="PhoneIcon" /> {{ data.phone  || '-' }}</p>
        <b-row class="mt-2">
          <b-col>
            <b-button
              variant="primary"
              class="mt-1"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              @click="toggleUpdate"
            >
              <feather-icon icon="EditIcon" /> {{ $t("updateProfile.title") }}
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-col>
    <b-col  class="col-12 col-md-6 mt-1 mt-md-0 col-md-3 col-12">
      <div >
        <b-card-title title-tag="h2" class="font-weight-bold mb-1">
        {{ $t("general.rating") }}
        </b-card-title>
        <div>
          <div v-for="(rating, index) in data.rating"
              :key="rating"
              :class="index < data.rating.length - 1 ? 'mb-1' : ''"
            class="personal-info "
           >
            <ul class="list-unstyled">
              <li class="mb-1">
                <feather-icon
                  icon="StarIcon"
                  size="22"
                  class="rating-star mr-1"
                  :class="{
                    active: i <= index+1,
                  }"
                  v-for="i in index+1"
                  :key="`star-${i}`"
                /> <strong>  {{ data.rating[index] }}</strong>  {{ $t('general.rating') }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </b-col>
  </b-row >
  </b-card>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BCardText,
  BLink,
  BRow,
  BCol,
  BButton,
  BAvatar,
  BAlert,
  BFormGroup,
  BFormRating
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import Repository from "@/repositories/RepositoryFactory";
import NButtonLoading from "@/components/NButtonLoading";
import NFormConfirmation from "@/components/NFormConfirmation";
import NInput from "@/components/NInput";
import FormInput from "./updateProfileFormInput";
import { mapValidationMessage } from "@/libs/helper";
import { avatarText } from "@core/utils/filter";
import NSingleImageUploader from "@/components/NSingleImageUploader";
import general from "@/router/routes/general";
import useJwt from '@/auth/jwt/useJwt';

const ProfileRepository = Repository.get("profile");
const FileRepository = Repository.get("file");

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardTitle,
    BCardText,
    BLink,
    BButton,
    BAvatar,
    BAlert,
    BFormGroup,
    NButtonLoading,
    NFormConfirmation,
    NInput,
    NSingleImageUploader,
    BFormRating
  },
  directives: {
    Ripple,
  },
  computed: {
    userData: function () {
      return this.$store.state.profile?.email || this.$store.state.profile?.phone
        ? this.$store.state.profile
        : JSON.parse(localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName));
    },
    isVerify: function () {
      if (this.$store.state.profile.isVerify) {
        return this.$store.state.profile.isVerify;
      }

      return this.userData.isVerify;
    },
    locale() {
      return this.$i18n.locale.split("-")[0];
    },
  },
  data() {
    return {
      data: JSON.parse(localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName)),
      isEmailChanged: false,
      loading: false,
      isUpdate: this.$route.query.isUpdate ? true : false,
      file: null,

      avatarText,
      rating: 0,
    };
  },
  methods: {
    getRoleName(roleNames) {
      if (!roleNames || !roleNames.length) return '';

      // Get unique role names
      const uniqueRoles = [...new Set(roleNames.map(role => {
        if (typeof role === 'string') {
          return role === 'Super Admin' ? this.$t('field.superAdmin') : role;
        }

        // If role is an object, get translation based on locale
        if (typeof role === 'object') {
          const name = role[this.locale] || role['en'] || Object.values(role)[0];
          return name === 'Super Admin' ? this.$t('field.superAdmin') : name;
        }

        return '';
      }).filter(Boolean))];

      return uniqueRoles.join(', ');
    },
    handleBindTelegram() {
      window.open(this.userData.bindTelegramUrl, "_blank");
    },
    onImageChange(file) {
      this.file = file;
    },
    toggleUpdate() {
      console.log("toggle click");
      this.isUpdate = !this.isUpdate;
      if (this.isUpdate) {
        this.$router.replace({ query: { isUpdate: 1 } });
      } else {
        this.$router.replace({ query: {} });
      }
    },
    validateForm() {
      this.$refs.updateProfileForm.validate().then((success) => {
        if (success) {
          if (this.data.image) {
            this.uploadImage();
          } else {
            this.submit();
          }
        }
      });
    },
    uploadImage() {
      this.loading = true;
      let formData = new FormData();
      formData.append("image", this.data.image);
      console.log("image: ", this.data.image);
      FileRepository.upload(formData)
        .then((response) => {
          const data = response?.data?.data;
          if (data) {
            this.data.mediaId = data.mediaId;
            this.submit();
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
    async submit() {
      this.loading = true;
      ProfileRepository.updateProfile({
        mediaId: this.data.mediaId,
        firstName: this.data.firstName,
        lastName: this.data.lastName,
        phone: this.data.phone,
        email: this.data.email,
        origin: this.data.origin,
      })
        .then((response) => {
          const user = response.data.data;
          this.data = {
            ...user,
          };
          localStorage.setItem(useJwt.jwtConfig.storageUserDataKeyName, JSON.stringify(user));
          this.$store.commit("profile/UPDATE_PROFILE", user);
          this.isEmailChanged = user.isEmailChanged;
          this.toggleUpdate();
        })
        .catch((error) => {
          console.log(error.response);
          if (error.response?.status === 422) {
            const errorMessage = mapValidationMessage(
              error.response.data.validate
            );
            this.$refs.updateProfileForm.setErrors(errorMessage);
          }
        })
        .then(() => {
          this.loading = false;
        });
    },
  },
  setup() {
    const fields = FormInput;
    return {
      fields,
    };
  },
};
</script>
